import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { showAlert } from 'actions/app';
import { KeywordPicker } from 'common';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import API from 'services/API';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { MULTIPLE_ACTIVITY_CREATION_SECTIONS } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  CandidateEntity,
  CompanyEntity,
  ContactEntity,
  EntityType
} from 'UI/constants/entityTypes';
import { getErrorMessage } from 'UI/utils';
import { prepareFormToSubmit } from 'UI/utils/forms';
import { DEFAULT_DISPLAY_TEXT_KEY, formatKeywordTags } from 'UI/utils/inventory';

const FORM_FIELDS_MAP = {
  KeywordTags: {
    key: 'keywordTags',
    outboundAccesor: keywordTags => formatKeywordTags(keywordTags)
  }
};

const getEntityLabel = ({ entityType, totalSelected }) => {
  if (!totalSelected || !totalSelected) return null;

  const isSingular = totalSelected === 1;
  const ENTITY_EXTRACTION_MAPPING = {
    [EntityType.Candidate]: isSingular ? CandidateEntity.singular : CandidateEntity.title,
    [EntityType.Contact]: isSingular ? ContactEntity.singular : ContactEntity.title,
    [EntityType.Company]: isSingular ? CompanyEntity.singular : CompanyEntity.title
  };

  return ENTITY_EXTRACTION_MAPPING[entityType];
};

export const KeywordsFormDrawer = ({
  endpoint,
  entityType = null,
  FPDrawerProps,
  onClose,
  selectedItems = []
}) => {
  const dispatch = useDispatch();
  const totalSelected = selectedItems.length;

  const [uiState, setUiState] = useState({
    isSaving: false
  });

  const { register, handleSubmit, setValue, watch, unregister } = useForm();
  const keywordTagsValue = watch(FORM_FIELDS_MAP.KeywordTags.key) ?? [];

  useEffect(() => {
    register({ name: FORM_FIELDS_MAP.KeywordTags.key });

    return () => {
      unregister(FORM_FIELDS_MAP.KeywordTags.key);
    };
  }, [register, unregister]);

  const saveForm = async formData => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));

      const response = await API.post(endpoint, {
        ...prepareFormToSubmit(formData, FORM_FIELDS_MAP),
        items: selectedItems
      });

      if (response.status === HTTPStatusCodes.Created) {
        dispatch(
          showAlert({
            severity: 'success',
            title: strings.formatString(
              strings.inventoryProfiles.sections.tabs.activityNote.creationAction,
              {
                section: MULTIPLE_ACTIVITY_CREATION_SECTIONS.keywords,
                action: 'Created'
              }
            )
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          severity: 'error',
          title: 'Error',
          body: getErrorMessage(error)
        })
      );
    } finally {
      setUiState(prev => ({ ...prev, isSaving: false }));
    }
  };

  const handleChangeKeywords = (name, value) => {
    setValue(name, value, true);
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: {
        md: 3
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: uiState.isSaving || !keywordTagsValue.length,
        onProgressText: strings.shared.ui.saving,
        isSaving: uiState.isSaving
      },
      GridItemProps: {
        md: 3
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: strings.inventoryProfiles.common.keywords.title }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(saveForm) }}
      FPDrawerProps={FPDrawerProps}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <Box mt={2} mb={3}>
        <Typography variant="subtitle2" component="h6" gutterBottom>
          {strings.formatString(strings.inventory.shared.listing.multipleActivityCreation.total, {
            activity: MULTIPLE_ACTIVITY_CREATION_SECTIONS.keywords,
            entity: getEntityLabel({ entityType, totalSelected }),
            total: totalSelected
          })}
        </Typography>
        <Divider />

        <KeywordPicker
          allowNewItems
          displayTextKey={DEFAULT_DISPLAY_TEXT_KEY}
          endpoint={Endpoints.KeywordTags}
          name={FORM_FIELDS_MAP.KeywordTags.key}
          onSelect={handleChangeKeywords}
          placeholder={strings.inventoryProfiles.common.keywords.placeholder}
          selectedValue={keywordTagsValue}
          typeaheadLimit={25}
        />
      </Box>
    </FPDrawer>
  );
};
